import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { CalendarDays, FileText, Activity, Receipt, ShieldCheck, AlertTriangle, Building2, PlayCircle } from 'lucide-react';
import ReferralBanner from '../../components/ReferralBanner';
import ResumeDraftsCard from '../../components/ResumeDraftsCard';
import PatientRecoveryTile from '../../components/PatientRecoveryTile';
import PractitionersScroller from '../../components/PractitionersScroller';
import RehabProgressSummary from '../../components/RehabProgressSummary';
import { SetupVideo } from '../ops/helpers';

export default function PatientDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [invs, setInvs] = useState([]);
  const [pres, setPres] = useState([]);
  const [completeness, setCompleteness] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [profile, setProfile] = useState(null);
  const [hasWelcomeVideo, setHasWelcomeVideo] = useState(false);

  // Note: the profile-completion *modal* is mounted globally in AppLayout so it
  // pops on every patient tab (not just the dashboard). We still load
  // `completeness` here so the in-page yellow banner stays in sync.
  useEffect(() => {
    api.get('/appointments').then(r => setAppts(r.data));
    api.get('/documents').then(r => setDocs(r.data));
    api.get('/invoices').then(r => setInvs(r.data));
    api.get('/exercises/prescriptions').then(r => setPres(r.data));
    api.get('/patients/me/completeness').then(r => setCompleteness(r.data)).catch(()=>{});
    api.get('/clinic/me').then(r => setClinic(r.data)).catch(()=>{});
    api.get('/patients/me').then(r => setProfile(r.data)).catch(()=>{});
    api.get('/clinic/settings').then(r => setHasWelcomeVideo(!!(r.data?.setup_videos || {})['patient-welcome'])).catch(()=>{});
  }, []);

  const upcoming = appts.filter(a => a.status === 'scheduled').slice(0, 3);
  const unpaid = invs.filter(i => i.status === 'unpaid');

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">{user?.locale?.toUpperCase()} · PATIENT</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Hello, {profile?.first_name || user?.name?.split(' ')[0]}</h1>
        <p className="text-fg-muted mt-2">Your therapy at a glance.</p>
      </div>

      <div className="grid md:grid-cols-12 gap-4">
        <ReferralBanner />

        {/* Your clinic — makes it explicit which clinic the patient books with. */}
        {clinic && (
          <div className="md:col-span-12 bento-card flex flex-wrap items-center gap-4" data-testid="dash-clinic-card">
            <div className="w-12 h-12 rounded-full bg-[#F2EBDB] flex items-center justify-center overflow-hidden flex-shrink-0">
              {clinic.logo_url
                ? <img src={clinic.logo_url} alt={clinic.name} className="w-full h-full object-cover"/>
                : <Building2 className="w-5 h-5 text-brand"/>}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">Your clinic</div>
              <div className="font-heading font-bold text-lg" data-testid="dash-clinic-name">{clinic.name}</div>
              {(clinic.tagline || clinic.city) && <div className="text-xs text-fg-muted">{clinic.tagline || clinic.city}</div>}
            </div>
            <Link to="/patient/book" className="btn-primary text-sm" data-testid="dash-clinic-book-btn">
              Book at {clinic.name}
            </Link>
          </div>
        )}

        {/* Welcome tour video — patient-facing training segment */}
        {hasWelcomeVideo && (
          <div className="md:col-span-12 bento-card" data-testid="patient-welcome-video-card">
            <div className="flex items-center gap-3 mb-1">
              <PlayCircle className="w-5 h-5 text-brand"/>
              <h3 className="font-heading font-bold text-xl">Welcome to your patient portal</h3>
            </div>
            <p className="text-sm text-fg-muted">A short guided tour of your appointments, documents, invoices and privacy controls — pick your audio and subtitle language below the player.</p>
            <SetupVideo videoKey="patient-welcome"/>
          </div>
        )}

        {/* Iter 70 — Resume drafts */}
        <div className="md:col-span-12">
          <ResumeDraftsCard role="patient"/>
        </div>

        {/* Iter 82 — Recovery % tile (same metric the GP sees) */}
        <div className="md:col-span-12">
          <PatientRecoveryTile />
        </div>

        {completeness && !completeness.complete && (
          <div className="md:col-span-12 surface border border-[#FFB000]/40 bg-[#FFB000]/10 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="dash-incomplete-banner">
            <AlertTriangle className="w-5 h-5 text-[#FFB000]"/>
            <div className="flex-1 min-w-[200px] text-sm">
              <strong>Your profile is incomplete.</strong>{' '}
              {completeness.missing.length} field(s) still required before you can book appointments.
            </div>
            <Link to="/patient/profile" className="btn-primary text-xs" data-testid="dash-complete-profile-btn">
              Complete now
            </Link>
          </div>
        )}
        <div className="md:col-span-7 bento-card">
          <div className="flex items-center gap-3 mb-4">
            <CalendarDays className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-bold text-xl">Upcoming sessions</h3>
          </div>
          {upcoming.length ? upcoming.map(a => (
            <div key={a.appointment_id} className="py-3 border-b border-soft last:border-0 flex items-center justify-between">
              <div>
                <div className="font-medium">{a.therapist_name}</div>
                <div className="text-xs text-fg-muted">{new Date(a.starts_at).toLocaleString()} · {a.duration_min} min</div>
              </div>
              <span className="badge-neutral">{a.status}</span>
            </div>
          )) : <div className="text-sm text-fg-muted">No upcoming sessions.</div>}
          <div className="mt-3 flex gap-3">
            <Link to="/patient/book" className="btn-primary text-xs inline-flex items-center gap-1" data-testid="dash-book-btn">
              Book a session
            </Link>
            <Link to="/patient/appointments" className="text-brand text-xs inline-flex items-center pt-1.5">All appointments →</Link>
          </div>
        </div>

        <div className="md:col-span-5 bento-card">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="w-5 h-5 text-accent" />
            <h3 className="font-heading font-bold text-xl">Outstanding balance</h3>
          </div>
          <div className="text-4xl font-heading font-bold">
            {unpaid.reduce((s, i)=>s+i.total, 0).toFixed(2)}
            <span className="text-base text-fg-muted ml-2 font-normal">{unpaid[0]?.currency || ''}</span>
          </div>
          <div className="text-xs text-fg-muted mt-1">{unpaid.length} unpaid invoice{unpaid.length===1?'':'s'}</div>
          <Link to="/patient/invoices" className="btn-accent text-sm mt-4 inline-block" data-testid="patient-pay-link">Settle now</Link>
        </div>

        <div className="md:col-span-5 bento-card">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-bold text-xl">Medical documents</h3>
          </div>
          <div className="text-3xl font-heading font-bold">{docs.length}</div>
          <div className="text-xs text-fg-muted mt-1">securely stored, GDPR-aligned</div>
          {/* Iter 133 — Standalone "Confidential Medical Documents" page removed.
              Docs are uploaded + browsed from inside the intake / interview so
              they stay attached to the patient's clinical record. */}
          <Link to="/patient/interview" className="text-brand text-xs mt-3 inline-block" data-testid="dash-docs-manage">Upload from intake →</Link>
        </div>

        <div className="md:col-span-7 bento-card">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-bold text-xl">Prescribed exercises</h3>
          </div>
          {pres[0]?.exercises?.length ? (
            <div className="grid grid-cols-3 gap-2">
              {pres[0].exercises.slice(0,3).map(ex => (
                <div key={ex.exercise_id} className="surface border border-soft rounded-md p-2">
                  <div className="text-xs font-medium">{ex.name}</div>
                  <div className="text-[10px] text-fg-muted">{ex.sets}×{ex.reps}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-fg-muted">No active prescription yet — your clinician will assign exercises after your visit.</div>}
          <Link to="/patient/hep" className="text-brand text-xs mt-3 inline-block">Open my exercises →</Link>
        </div>

        {/* Iter 132 — Practitioners moved from a standalone page into a
            compact horizontal scroller on the dashboard so patients can
            discover the whole team without leaving their home screen. */}
        <div className="md:col-span-12">
          <PractitionersScroller/>
        </div>

        {/* Iter 133 — Rehab progress summary — 3-stat glance at streak,
            weekly adherence and last VAS. Tap to open the full combined
            Rehab progress page. */}
        <div className="md:col-span-6">
          <RehabProgressSummary/>
        </div>

        <div className="md:col-span-12 bento-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#F2EBDB] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-brand" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold">Your privacy is protected</div>
            <div className="text-sm text-fg-muted">We follow GDPR (EU), Polish UODO, and HIPAA-aligned controls. You can review your consents at any time.</div>
          </div>
          <Link to="/patient/profile" className="btn-secondary text-sm">Review</Link>
        </div>
      </div>
    </div>
  );
}
